import * as MyBrandsTypes from './myBrands.types';
import * as api from 'api/endpoints/myBrands/myBrands.api';
import { myBrandsError } from 'utils/errorCodes';
import { notificationErr } from 'views/components/UI/notification';
import {
  messageError,
  messageSuccess,
  messageWarning,
} from 'views/components/UI/message';
import { createNewBrandAPI } from 'api/endpoints/brands/brands.api';
import {
  IMyBrand,
  IMyBrandsBrandPointOfSale,
  ITenant,
  TCreateNewBrandProps,
} from 'interfaces/myBrands.interface';
import { getAssociationStateName } from 'utils/getAssociationStateName';
import {
  IaddToMyBrandsBody,
  IgetBrandsAPIResponseDataItem,
  IGetMyBrandsAPIResponse,
  TgetTenantPointsOfSaleAPIResponseItem,
} from 'api/endpoints/myBrands/myBrands.response.interface';
import { getCatalogAPI } from 'api/endpoints/catalog/catalog.api';
import { connected, IAssociationsStates, ITranslate } from 'interfaces/general.interface';
import { IBodyProductSearch } from 'interfaces/catalog.interface';
import { TmyBrandsDispatchReducer } from './myBrands.reducer';

export async function getMyBrandsAction({
  dispatch,
  clientId,
  token,
  translate,
  associationStates,
}: {
  dispatch: React.Dispatch<TmyBrandsDispatchReducer>;
  clientId: string;
  token: string;
  translate: ITranslate;
  associationStates: IAssociationsStates;
}): Promise<boolean> {
  dispatch({ type: MyBrandsTypes.SET_IS_LOADING_GET_MY_BRANDS, payload: true });
  try {
    const [connectedBrandsFetch, pendingBrandsFetch] = await Promise.all([
      api.getMyBrandsAPI({ clientId, stateId: associationStates.connected.id, token }),
      api.getMyBrandsAPI({ clientId, stateId: associationStates.pending.id, token }),
      // api.getMyBrandsAPI(clientId, associationStates.rejected.id, token),
      // api.getMyBrandsAPI(clientId, associationStates.disconnected.id, token),
    ]);

    let myBrands: IMyBrand[] = [];

    // Connected brands
    if (
      connectedBrandsFetch.response.status === 200 &&
      connectedBrandsFetch.data.length > 0
    ) {
      const connectedBrands = await getAndNormalizeMyBrands({
        myBrandsData: connectedBrandsFetch,
        associationStates,
        clientId,
        token,
        type: 'connected',
      });
      if (connectedBrands.length > 0) {
        myBrands = [...myBrands, ...connectedBrands];

        // Get all points of sale connected
        let pointsOfSaleConnected: IMyBrandsBrandPointOfSale[] = [];
        for (const item of connectedBrands) {
          if (item.tenant.pointsOfSale?.length > 0) {
            for (const point of item.tenant.pointsOfSale) {
              point.associated === true && pointsOfSaleConnected.push(point);
            }
          }
        }
        if (pointsOfSaleConnected.length > 0) {
          dispatch({
            type: MyBrandsTypes.SET_POINTS_OF_SALE_CONNECTED,
            payload: pointsOfSaleConnected,
          });
        }
      }
    }

    // Pending brands
    if (
      pendingBrandsFetch.response.status === 200 &&
      pendingBrandsFetch.data.length > 0
    ) {
      const pendingBrands = await getAndNormalizeMyBrands({
        myBrandsData: pendingBrandsFetch,
        associationStates,
        clientId,
        token,
        type: 'pending',
      });
      if (pendingBrands.length > 0) {
        myBrands = [...myBrands, ...pendingBrands];
      }
    }

    if (myBrands.length > 0) {
      dispatch({
        type: MyBrandsTypes.GET_MY_BRANDS,
        payload: myBrands,
      });
    }
    return true;
  } catch (err) {
    messageError(translate('action_my-brands_get-my-brands_error'));
    notificationErr(myBrandsError.get_my_brands_001, translate);
    return false;
  } finally {
    dispatch({ type: MyBrandsTypes.SET_IS_LOADING_GET_MY_BRANDS, payload: false });
  }
}

export async function addToMyBrandAction({
  dispatch,
  brandId,
  tenantId,
  clientId,
  token,
  translate,
  associationStates,
  countryId,
}: {
  dispatch: React.Dispatch<TmyBrandsDispatchReducer>;
  brandId: string;
  tenantId: string;
  clientId: string;
  token: string;
  translate: ITranslate;
  associationStates: IAssociationsStates;
  countryId: string;
}): Promise<{ ok: boolean; haveCatalog: boolean }> {
  if (!brandId) return { ok: false, haveCatalog: false };
  dispatch({
    type: MyBrandsTypes.SET_IS_LOADING_ADD_TO_MY_BRAND,
    payload: {
      isLoading: true,
      tenantId,
    },
  });
  let body: IaddToMyBrandsBody = {
    tenant_id: undefined,
    client_id: clientId,
  };

  body.tenant_id = tenantId;

  try {
    const createFetch = await api.addToMyBrandsAPI({ body, token });
    if (createFetch.response.status === 403) {
      messageWarning(translate('not-authorized-warning'));
      return { ok: false, haveCatalog: false };
    }
    if (createFetch.response.status === 404) {
      notificationErr(myBrandsError.add_to_my_brands_004, translate);
      messageError(translate('action_my-brands_add-to-my-brands_error'));
      return { ok: false, haveCatalog: false };
    }
    try {
      const [connectedBrandsFetch, pendingBrandsFetch] = await Promise.all([
        api.getMyBrandsAPI({ clientId, stateId: associationStates.connected.id, token }),
        api.getMyBrandsAPI({ clientId, stateId: associationStates.pending.id, token }),
        // api.getMyBrandsAPI(clientId, associationStates.rejected.id, token),
        // api.getMyBrandsAPI(clientId, associationStates.disconnected.id, token),
      ]);
      const connectedBrands = await getAndNormalizeMyBrands({
        myBrandsData: connectedBrandsFetch,
        associationStates,
        clientId,
        token,
        type: 'connected',
      });
      const pendingBrands = await getAndNormalizeMyBrands({
        myBrandsData: pendingBrandsFetch,
        associationStates,
        clientId,
        token,
        type: 'pending',
      });
      // const rejectedBrands: IMyBrand[] = await getMyBrands(rejectedBrandsFetch, associationStates, countryId, clientId, token)
      // const disconnectedBrands: IMyBrand[] = await getMyBrands(disconnectedBrandsFetch, associationStates, countryId, clientId, token, 'disconnected')
      const myBrands = [...connectedBrands, ...pendingBrands];

      dispatch({
        type: MyBrandsTypes.ADD_TO_MY_BRAND,
        payload: myBrands,
      });

      // Check if the brand have catalog
      const catalogBody: IBodyProductSearch = {
        options: {
          brand_id: [brandId],
          client_id: null,
          csv_format: false,
          favorite: '',
          with_images: null,
          search: [],
          language_id: null,
          order: [],
          group: [],
          limit: 10,
          index: 0,
        },
        filter: {
          reference: [],
          color: [],
          season: [],
          segmentation: [],
          division: [],
          family: [],
          gender: [],
          pvi: [],
          pvpr: [],
          tag: [],
          tier: [],
        },
      };
      const getTenantCatalogs = await getCatalogAPI({ body: catalogBody, token });
      if (
        getTenantCatalogs.response.status === 200 ||
        getTenantCatalogs.response.status === 404
      ) {
        messageSuccess(translate('action_my-brands_add-to-my-brands_success'));
        if (getTenantCatalogs.data?.data?.length === 0) {
          return { ok: true, haveCatalog: false };
        } else {
          return { ok: true, haveCatalog: true };
        }
      }
      return { ok: false, haveCatalog: false };
    } catch (err) {
      messageError(translate('action_my-brands_add-to-my-brands_error'));
      notificationErr(myBrandsError.add_to_my_brands_001, translate);
      return { ok: false, haveCatalog: false };
    }
  } catch (err) {
    messageError(translate('action_my-brands_add-to-my-brands_error'));
    notificationErr(myBrandsError.add_to_my_brands_002, translate);
    return { ok: false, haveCatalog: false };
  } finally {
    dispatch({
      type: MyBrandsTypes.SET_IS_LOADING_ADD_TO_MY_BRAND,
      payload: {
        isLoading: false,
        tenantId: '',
      },
    });
  }
}

export async function addToMyBrandMultipleAction({
  dispatch,
  myBrands,
  brands,
  clientId,
  token,
  translate,
  associationStates,
  countryId,
}: {
  dispatch: React.Dispatch<TmyBrandsDispatchReducer>;
  myBrands: IMyBrand[];
  brands: IgetBrandsAPIResponseDataItem[];
  clientId: string;
  token: string;
  translate: ITranslate;
  associationStates: IAssociationsStates;
  countryId: string;
}) {
  dispatch({
    type: MyBrandsTypes.SET_ADD_TO_MY_BRAND_MULTIPLE_STATUS,
    payload: { isLoading: true },
  });
  let totalBrands = brands.length;
  let haveToAdd = true;
  let brandsToAddCount = 0;
  let brandsToAdd: IgetBrandsAPIResponseDataItem[] = [];
  let brandsAddedCount = 0;
  if (totalBrands === 0) {
    dispatch({
      type: MyBrandsTypes.SET_ADD_TO_MY_BRAND_MULTIPLE_STATUS,
      payload: {
        haveBrandsToAdd: false,
      },
    });
  }
  const myBrandsIds = myBrands.map((item: IMyBrand) => item.tenant.brand.id);
  for (const brand of brands) {
    if (
      brand.associated === false &&
      brand.tenants.length > 0 &&
      !myBrandsIds.includes(brand.id)
    ) {
      brandsToAddCount++;
      haveToAdd = true;
      brandsToAdd.push(brand);
    }
  }
  dispatch({
    type: MyBrandsTypes.SET_ADD_TO_MY_BRAND_MULTIPLE_STATUS,
    payload: {
      haveBrandsToAdd: haveToAdd,
      totalBrands: totalBrands,
      brandsToAdd: brandsToAddCount,
    },
  });
  if (haveToAdd === true) {
    dispatch({
      type: MyBrandsTypes.SET_ADD_TO_MY_BRAND_MULTIPLE_STATUS,
      payload: { isOpenModal: true },
    });
    setTimeout(async () => {
      try {
        for (const brand of brandsToAdd) {
          const findTenantByCountryId = brand.tenants.find(
            (item) => item.country_id === countryId
          );
          const body: IaddToMyBrandsBody = {
            tenant_id: findTenantByCountryId
              ? findTenantByCountryId.id
              : brand.tenants[0].id,
            client_id: clientId,
          };
          try {
            const createFetch = await api.addToMyBrandsAPI({ body, token });
            if (
              createFetch.response.status === 200 ||
              createFetch.response.status === 201
            ) {
              brandsAddedCount++;
              dispatch({
                type: MyBrandsTypes.SET_ADD_TO_MY_BRAND_MULTIPLE_STATUS,
                payload: {
                  brandsAdded: brandsAddedCount,
                },
              });
            }
          } catch (error) {
            console.log(error);
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        dispatch({
          type: MyBrandsTypes.SET_ADD_TO_MY_BRAND_MULTIPLE_STATUS,
          payload: {
            isLoading: false,
          },
        });
        try {
          const [connectedBrandsFetch, pendingBrandsFetch] = await Promise.all([
            api.getMyBrandsAPI({
              clientId,
              stateId: associationStates.connected.id,
              token,
            }),
            api.getMyBrandsAPI({
              clientId,
              stateId: associationStates.pending.id,
              token,
            }),
            // api.getMyBrandsAPI(clientId, associationStates.rejected.id, token),
            // api.getMyBrandsAPI(clientId, associationStates.disconnected.id, token),
          ]);
          const connectedBrands: IMyBrand[] = await getAndNormalizeMyBrands({
            myBrandsData: connectedBrandsFetch,
            associationStates,
            clientId,
            token,
            type: 'connected',
          });
          const pendingBrands: IMyBrand[] = await getAndNormalizeMyBrands({
            myBrandsData: pendingBrandsFetch,
            associationStates,
            clientId,
            token,
            type: 'pending',
          });
          // const rejectedBrands: IMyBrand[] = await getMyBrands(rejectedBrandsFetch, associationStates, countryId, clientId, token)
          // const disconnectedBrands: IMyBrand[] = await getMyBrands(disconnectedBrandsFetch, associationStates, countryId, clientId, token, 'disconnected')
          const myBrands = [...connectedBrands, ...pendingBrands];

          dispatch({
            type: MyBrandsTypes.ADD_TO_MY_BRAND,
            payload: myBrands,
          });
          messageSuccess(`${brandsAddedCount} solicitudes de asociaciones enviadas`);
          return dispatch({
            type: MyBrandsTypes.SET_ADD_TO_MY_BRAND_MULTIPLE_STATUS,
            payload: {
              isOpenModal: false,
              totalBrands: 0,
              brandsToAdd: 0,
              brandsAdded: 0,
            },
          });
        } catch (err) {
          messageError(translate('action_my-brands_add-to-my-brands_error'));
          notificationErr(myBrandsError.add_to_my_brands_multiple_001, translate);
        }
      }
    }, 1500);
  }
}

export async function removeFromMyBrandAction({
  dispatch,
  associationId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TmyBrandsDispatchReducer>;
  associationId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const removeFetch = await api.removeFromMyBrandAPI({ associationId, token });
    if (removeFetch.response.status === 200) {
      dispatch({
        type: MyBrandsTypes.REMOVE_FROM_MY_BRAND,
        payload: associationId,
      });
      messageSuccess(translate('action_my-brands_remove-from-my-brands_success'));
      return true;
    } else {
      messageError(translate('action_my-brands_remove-from-my-brands_error'));
      notificationErr(myBrandsError.remove_my_from_my_brands_001, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('action_my-brands_remove-from-my-brands_error'));
    notificationErr(myBrandsError.remove_my_from_my_brands_002, translate);
    return false;
  }
}

export async function removeFromMyBrandsMultipleAction({
  dispatch,
  associationsIds,
  myBrands,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TmyBrandsDispatchReducer>;
  associationsIds: string[];
  myBrands: IMyBrand[];
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  let brandsAssociated: number = 0;
  try {
    for (const associationId of associationsIds) {
      const removeFetch = await api.removeFromMyBrandAPI({ associationId, token });
      if (removeFetch.response.status === 200) {
        brandsAssociated++;
      }
    }
    if (brandsAssociated === associationsIds.length) {
      messageSuccess(
        translate('action_my-brands_remove-from-my-brands-multiple_success')
      );
      dispatch({
        type: MyBrandsTypes.REMOVE_FROM_MY_BRAND_MULTIPLE,
        payload: myBrands.filter((item) => !associationsIds.includes(item.key)),
      });
      return true;
    } else {
      messageError(translate('action_my-brands_remove-from-my-brands_error'));
      notificationErr(myBrandsError.remove_my_from_my_brands_multiple_001, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('action_my-brands_remove-from-my-brands_error'));
    notificationErr(myBrandsError.remove_my_from_my_brands_multiple_002, translate);
    return false;
  }
}

export async function createNewBrandAction({
  dispatch,
  brand,
  clientId,
  dbUserName,
  token,
  translate,
  associationStates,
}: {
  dispatch: React.Dispatch<TmyBrandsDispatchReducer>;
  brand: TCreateNewBrandProps;
  clientId: string;
  dbUserName: string;
  token: string;
  translate: ITranslate;
  associationStates: IAssociationsStates;
}): Promise<boolean> {
  try {
    const createBrandFetch = await createNewBrandAPI(brand, clientId, dbUserName, token);
    if (createBrandFetch.response.status === 200) {
      const {
        id,
        client_id,
        state_id,
        client_ok,
        createdAt,
        updatedAt,
        client_alias_id,
        brand,
      } = createBrandFetch.data.association;
      // const { contact_name, contact_prefix, contact_suffix, contact_email, address, city } = createBrandFetch.data.alias
      const newBrand: IMyBrand = {
        key: id,
        id,
        client_id: client_id,
        state_id: state_id,
        state_name: getAssociationStateName(
          createBrandFetch.data.association.state_id,
          associationStates
        ),
        client_ok: client_ok,
        createdAt: createdAt,
        updatedAt: updatedAt,
        client_alias_id: client_alias_id,
        tenant_id: '',
        tenant_ok: false,
        tenant: {
          pointsOfSale: [] as IMyBrandsBrandPointOfSale[],
          brand,
        } as ITenant,
        emailResend: false,
        has_users: false,
        client_alias: null,
      };
      messageSuccess(translate('action_my-brands_create-new-brand_success'));
      dispatch({ type: MyBrandsTypes.CREATE_NEW_BRAND, payload: newBrand });
      return true;
    }
    messageWarning(translate('action_my-brands_create-new-brand_exists'));
    return false;
  } catch (err) {
    console.log(err);
    return false;
  }
}

export async function resendAssociationEmailAction({
  dispatch,
  tenantId,
  to,
  clientId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TmyBrandsDispatchReducer>;
  tenantId: string;
  to?: { email: string; name: string };
  clientId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({
    type: MyBrandsTypes.SET_IS_LOADING_RESEND_EMAIL_VERIFICATION,
    payload: {
      isLoading: true,
      tenantId,
    },
  });
  try {
    const resendFetch = await api.resendAssociationEmailAPI({
      tenantId,
      clientId,
      to,
      token,
    });
    if (resendFetch.response.status === 200) {
      messageSuccess(translate('action_my-brands_resend-association-email_success'));
      dispatch({
        type: MyBrandsTypes.EMAIL_RESEND,
        payload: {
          id: tenantId,
          emailResend: true,
        },
      });
      return true;
    }
    messageError(translate('action_my-brands_resend-association-email_error'));
    notificationErr(myBrandsError.resend_association_email_001, translate);
    return false;
  } catch (err) {
    messageError(translate('action_my-brands_resend-association-email_error'));
    notificationErr(myBrandsError.resend_association_email_002, translate);
    return false;
  } finally {
    dispatch({
      type: MyBrandsTypes.SET_IS_LOADING_RESEND_EMAIL_VERIFICATION,
      payload: {
        isLoading: false,
        tenantId: null,
      },
    });
  }
}

/////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////
async function getAndNormalizeMyBrands({
  myBrandsData,
  associationStates,
  clientId,
  token,
  type,
}: {
  myBrandsData: IGetMyBrandsAPIResponse;
  associationStates: IAssociationsStates;
  clientId: string;
  token: string;
  type: string;
}): Promise<IMyBrand[]> {
  let orderedMyBrands: IMyBrand[] = [];
  let myBrandObj: IMyBrand | null = null;
  if (myBrandsData.response.status === 404) return [];
  for (const item of myBrandsData.data) {
    try {
      let pointsOfSale: Array<TgetTenantPointsOfSaleAPIResponseItem> = [];
      if (type === connected) {
        const [pointsOfSaleFetch] = await Promise.all([
          api.getTenantPointsOfSaleAPI({ clientId, tenantId: item.tenant.id, token }),
        ]);
        pointsOfSale = pointsOfSaleFetch.data ?? [];
      }
      myBrandObj = {
        ...item,
        key: item.id,
        state_name: getAssociationStateName(item.state_id, associationStates),
        tenant: {
          ...item.tenant,
          pointsOfSale,
        },
        emailResend: false,
      };
    } catch (err) {
      console.log(err);
    }
    myBrandObj && orderedMyBrands.push(myBrandObj);
  }
  return orderedMyBrands ?? [];
}
